import { Device } from '@twilio/voice-sdk'
import { localStorageService } from '../services/localStorage'
import toastService from '../services/toastService'
import { CUSTOM_EVENT_TYPES } from '../constants/matrixConst'
import { store } from '../redux/store'
import { toggleTriggered } from '../redux/reducers/pushSlice'
import { api } from '../../src/api'
import { dispatchCustomEvent } from '../utils/fireEvents'
import {
  resetCall,
  setCallSlot,
  setCurrentCall,
  setCurrentCallSid,
  setIsInCall,
  setIsLocalHold,
  addLocalCall,
  updateLocalCall,
  removeLocalCall,
  setActiveLocalCall,
} from '../redux/reducers/callSlice'
import { closeModal } from '../redux/reducers/modalSlice'
import { getCallParam } from '../utils/twilioCall'

export const CALL_STATUS_TYPE = {
  end_and_accept: 'end_and_accept',
  no_call: '',
  hold_and_accept: 'hold_and_accept',
}

class InitTwilioVoiceSingleton {
  static instance = null

  device = null
  tokenPromise = null

  isInitializing = false
  isInitialized = false
  isUpdatingToken = false
  isHangingUp = false
  isLoggingOut = false

  constructor() {
    if (InitTwilioVoiceSingleton.instance) {
      return InitTwilioVoiceSingleton.instance
    }

    this.setConnection = this.setConnection.bind(this)
    this.handleTokenWillExpire = this.handleTokenWillExpire.bind(this)
    this.handleOffline = this.handleOffline.bind(this)
    this.muteCall = this.muteCall.bind(this)
    this.unmuteCall = this.unmuteCall.bind(this)

    InitTwilioVoiceSingleton.instance = this
  }

  /* =========================
     INIT
  ========================== */

  async init() {
    if (this.device || this.isInitializing) return

    this.isInitializing = true

    try {
      const tokenData = await this.getToken()
      if (!tokenData?.token) throw new Error('No Twilio token')

      this.device = new Device(tokenData.token, {
        logLevel: 1, //change to 5 for production "silent" mode
        codecPreferences: ['opus', 'pcmu'],
      })

      this.attachDeviceEvents()

      await this.device.register()
      this.isInitialized = true

      console.log('✅ Twilio Device registered')
    } catch (err) {
      console.error('❌ Twilio init failed', err)
      toastService.show('error', 'Twilio init failed')
      this.device = null
    } finally {
      this.isInitializing = false
    }
  }

  /* =========================
     EVENTS
  ========================== */

  attachDeviceEvents() {
    if (!this.device) return
    // Voice SDK 2.x events: 'registered'/'unregistered' (1.x used 'ready'/'offline')
    this.device.on('registered', () => {
      console.log('📡 Twilio Device registered, state:', this.device?.state)
    })

    this.device.on('incoming', this.setConnection)
    this.device.on('tokenWillExpire', this.handleTokenWillExpire)
    this.device.on('unregistered', this.handleOffline)

    this.device.on('error', (error) => {
      console.error(`❌ Twilio error [${error?.code}]:`, error?.message, error)
      toastService.show('error', `Twilio error ${error?.code || ''}: ${error?.message}`)
    })
  }
  attachConnectionEvents(connection, direction = 'unknown') {
    if (!connection || connection._airtouchEventsAttached) return
    connection._airtouchEventsAttached = true

    console.log(`📞 Attaching connection events (${direction})`)

    // ✅ CALLEE ANSWERED
    connection.on('accept', () => {
      console.log(`✅ Call accepted by remote (${direction})`)
      // Backend's callSid of record is the parent leg: our client leg for outgoing,
      // the caller's leg (passed as custom parameter) for incoming.
      const callSid = this.getCallSid(connection)
      if (!callSid) return
      store.dispatch(updateLocalCall({ id: connection._airtouchId, callSid }))
      if (connection._airtouchId === this.activeCallId) store.dispatch(setCurrentCallSid(callSid))
    })

    // ❌ CALLEE REJECTED (busy / decline)
    connection.on('reject', () => {
      console.log(`❌ Call rejected by remote (${direction})`)
      this.notifyForCancel()
      this.removeCall(connection)
    })

    // ☎️ CALLER CANCELED BEFORE ANSWER
    connection.on('cancel', () => {
      console.log(`☎️ Call canceled before answer (${direction})`)
      this.notifyForCancel()
      this.removeCall(connection)
    })

    // 📴 CALL ENDED (either side)
    connection.on('disconnect', () => {
      console.log(`📴 Call disconnected (${direction})`)
      this.removeCall(connection)
    })

    connection.on('error', (err) => {
      console.error(`❌ Call error (${direction}) [${err?.code}]:`, err?.message, err)
      this.removeCall(connection)
    })
  }
  /* =========================
   UI helper
  ========================== */
  notifyForCancel = () => {
    console.log('☎️ Call canceled by caller')
    dispatchCustomEvent(CUSTOM_EVENT_TYPES.cancelCall)
    store.dispatch(closeModal())
  }
  /* =========================
     TOKEN
  ========================== */

  async getToken() {
    if (this.tokenPromise) return this.tokenPromise

    this.tokenPromise = (async () => {
      try {
        const userId = localStorageService.get('sci')
        const response = await store.dispatch(api.endpoints.getCallToken.initiate(userId))

        if (!response.data?.token) throw new Error('Token missing')
        return response.data
      } catch (err) {
        console.error('Token fetch failed', err)
        toastService.show('error', 'Error fetching Twilio token')
        return null
      } finally {
        setTimeout(() => {
          this.tokenPromise = null
        }, 500)
      }
    })()

    return this.tokenPromise
  }

  async updateToken(token) {
    if (this.device) {
      await this.device.updateToken(token)
      // Optionally re-register if needed
      if (typeof this.device.register === 'function' && this.device.state !== 'registered') {
        await this.device.register()
      }
    }
  }
  handleTokenWillExpire = async () => {
    if (this.isUpdatingToken || !this.device) return

    this.isUpdatingToken = true
    try {
      const tokenData = await this.getToken()
      if (tokenData?.token) {
        this.device.updateToken(tokenData.token)
        this.secondaryDevices.forEach((device) => device.updateToken(tokenData.token))
      }
    } finally {
      this.isUpdatingToken = false
    }
  }

  handleOffline = async () => {
    // destroy() (logout) also emits 'unregistered' — nothing to refresh then
    if (!this.device || this.isLoggingOut) return
    console.warn('⚠️ Device unregistered, refreshing token')
    this.handleTokenWillExpire()
  }

  /* =========================
     CALL HANDLING
     Several local Twilio Call objects can exist at once (iOS CallManager.calls model):
     one is active, the rest are on local hold. `this.calls` maps our id -> Call.
     A Twilio Device allows a single active Call, so every extra call runs on its own
     unregistered secondary Device (same token; only the primary one receives incoming calls).
  ========================== */

  calls = new Map()
  secondaryDevices = new Map() // call id -> Device
  activeCallId = null

  async createSecondaryDevice() {
    const tokenData = await this.getToken()
    if (!tokenData?.token) throw new Error('No Twilio token')
    const device = new Device(tokenData.token, {
      logLevel: 1,
      codecPreferences: ['opus', 'pcmu'],
    })
    device.on('error', (error) => {
      console.error(`❌ Twilio (secondary) error [${error?.code}]:`, error?.message, error)
    })
    return device
  }

  // Backwards-compatible accessor: the active (or pending incoming) Call
  get connection() {
    return this.activeCallId ? this.calls.get(this.activeCallId) || null : null
  }

  set connection(call) {
    if (!call) {
      this.activeCallId = null
      return
    }
    this.activeCallId = this.registerCall(call)
  }

  registerCall(call) {
    if (!call._airtouchId) {
      call._airtouchId =
        typeof crypto?.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`
    }
    this.calls.set(call._airtouchId, call)
    return call._airtouchId
  }

  getCallById(id) {
    return this.calls.get(id) || null
  }

  hasCalls() {
    return this.calls.size > 0
  }

  setConnection(connection) {
    const { isInCall, isInVideoCall } = store.getState().calls

    console.log('📲 Incoming call')

    // While busy the backend runs the call-waiting flow (new_voice_call push -> IncomingCall
    // modal with hold/merge/transfer), so the raw Twilio invite is refused here.
    if ((isInCall || isInVideoCall) && connection.direction === 'INCOMING') {
      connection.reject()
      return
    }

    connection._airtouchDevice = this.device
    this.attachConnectionEvents(connection, 'incoming')

    this.connection = connection
    dispatchCustomEvent(CUSTOM_EVENT_TYPES.incommingCall)
  }

  acceptCall(callData) {
    const call = this.connection
    console.log('🎛️ ACTION: acceptCall', { callData, liveCallSid: this.getCallSid(call) })
    call?.accept()

    // `direction` decides whether a blind transfer is possible: the backend redirects the leg
    // it knows, which is the *caller's* leg only for incoming calls (see handleTransfer).
    const incomingCallData = { ...callData, direction: 'incoming' }

    store.dispatch(toggleTriggered())
    store.dispatch(setIsInCall(true))
    store.dispatch(setCurrentCall(incomingCallData))
    store.dispatch(
      setCallSlot({
        ...incomingCallData,
        participants: [],
        isOnHold: false,
      }),
    )
    if (call) {
      store.dispatch(
        addLocalCall({
          id: call._airtouchId,
          callSid: this.getCallSid(call),
          ...incomingCallData,
        }),
      )
      store.dispatch(setActiveLocalCall(call._airtouchId))
    }
  }

  async connectDevice(params) {
    if (!this.device) {
      await this.init()
    }

    if (!this.device) return

    // A pending (unanswered) call blocks a new one; an active one is put on hold instead,
    // like iOS performStartCallAction.
    const active = this.connection
    if (active && active.status?.() === 'pending') return
    if (active) this.holdCall(this.activeCallId)

    console.log('📞 Starting outgoing call', params)

    // Primary device is free only while it has no call; otherwise dial on a secondary one
    const primaryBusy = Array.from(this.calls.values()).some(
      (call) => call._airtouchDevice === this.device,
    )
    const device = primaryBusy ? await this.createSecondaryDevice() : this.device
    const connection = await device.connect({ params })
    connection._airtouchDevice = device
    this.attachConnectionEvents(connection, 'outgoing')

    this.connection = connection
    if (device !== this.device) this.secondaryDevices.set(connection._airtouchId, device)

    // Raw number dialled from the keypad has no caller/callerId — fall back to the dialled number
    const callData = {
      caller: params?.caller || params?.To,
      callerId: params?.callerId || params?.To,
      organizationName: params?.organizationName,
      direction: 'outgoing',
    }

    store.dispatch(setCurrentCall(callData))

    store.dispatch(
      setCallSlot({
        ...params,
        ...callData,
        participants: [],
        isOnHold: false,
      }),
    )
    store.dispatch(addLocalCall({ id: connection._airtouchId, ...callData }))
    store.dispatch(setActiveLocalCall(connection._airtouchId))
  }

  // Make another local call the active one: hold the current, resume the target
  switchToCall(id) {
    const target = this.getCallById(id)
    if (!target || id === this.activeCallId) return
    if (this.activeCallId) this.holdCall(this.activeCallId)
    this.activeCallId = id
    this.setCallMediaEnabled(target, true)
    store.dispatch(setActiveLocalCall(id))
    dispatchCustomEvent(CUSTOM_EVENT_TYPES.unmuteCall)
  }

  /* =========================
     HANGUP
  ========================== */

  // Ends the active call. Returns true when another local call remains (and became active).
  handleReject = () => {
    const call = this.connection
    if (this.isHangingUp || !call) return this.hasCalls()
    this.isHangingUp = true

    try {
      const status = call.status()
      console.log('📴 Hanging up, status:', status)

      if (status === 'pending') {
        call.reject() // cancel before answer
      } else {
        call.disconnect() // end active call
      }
    } catch (err) {
      console.warn('Call already closed')
    } finally {
      // cleanup will also happen via disconnect event,
      // but we guard against missed events
      this.removeCall(call)
      this.isHangingUp = false
    }
    return this.hasCalls()
  }

  hangupAll() {
    for (const call of Array.from(this.calls.values())) {
      try {
        call.status() === 'pending' ? call.reject() : call.disconnect()
      } catch (err) {
        /* already closed */
      }
    }
    this.calls.clear()
    for (const id of Array.from(this.secondaryDevices.keys())) this.destroySecondaryDevice(id)
    this.activeCallId = null
    this.cleanupCall()
  }

  destroySecondaryDevice(id) {
    const device = this.secondaryDevices.get(id)
    if (!device) return
    this.secondaryDevices.delete(id)
    try {
      device.removeAllListeners()
      device.destroy()
    } catch (err) {
      /* already destroyed */
    }
  }

  // Drops one Call; the next remaining call becomes active, or everything is reset
  removeCall(call) {
    const id = call?._airtouchId
    if (!id || !this.calls.has(id)) return
    this.calls.delete(id)
    store.dispatch(removeLocalCall(id))
    this.destroySecondaryDevice(id)

    if (this.activeCallId !== id) return
    this.activeCallId = null

    const next = this.calls.keys().next().value
    if (next) {
      console.log('🔁 Active call ended, switching to held call')
      this.switchToCall(next)
    } else {
      this.cleanupCall()
    }
  }

  cleanupCall() {
    if (this.hasCalls()) return
    console.log('🧹 Cleaning up call')
    this.activeCallId = null
    store.dispatch(resetCall())
    store.dispatch(closeModal())
  }

  /* =========================
     AUDIO
  ========================== */

  // device.activeConnection() is a 1.x API; in 2.x active calls live in device.calls
  getActiveCall() {
    return this.connection || this.device?.calls?.[0] || null
  }

  // Incoming: the backend passes the caller's (parent) leg as the `callSid` parameter — that is
  // the leg every newCall/* endpoint expects. Outgoing: our own client leg is the call of record.
  getCallSid(connection = this.getActiveCall()) {
    if (!connection) return null
    return getCallParam(connection, 'callSid') || connection.parameters?.CallSid || null
  }

  getActiveCallSid() {
    return store.getState().calls.currentCall?.callSid || this.getCallSid()
  }

  muteCall = () => {
    const conn = this.getActiveCall()

    conn?.mute(true)
    dispatchCustomEvent(CUSTOM_EVENT_TYPES.muteCall)
  }

  unmuteCall = () => {
    const conn = this.getActiveCall()

    conn?.mute(false)
    dispatchCustomEvent(CUSTOM_EVENT_TYPES.unmuteCall)
  }

  // Twilio JS has no hold: pause our mic and the remote audio tracks of that call
  setCallMediaEnabled(call, enabled) {
    if (!call) return
    try {
      call.mute(!enabled)
    } catch (err) {
      /* call not connected yet */
    }
    call
      .getRemoteStream?.()
      ?.getAudioTracks()
      .forEach((track) => {
        track.enabled = enabled
      })
  }

  // Local hold, same as iOS `call.isOnHold`: no server-side hold music.
  // Backend `newCall/hold` redirects an *unanswered* incoming leg and must not be used here.
  holdCall = (id = this.activeCallId) => {
    const call = this.getCallById(id)
    if (!call) return
    this.setCallMediaEnabled(call, false)
    store.dispatch(updateLocalCall({ id, isOnHold: true }))
    if (id === this.activeCallId) store.dispatch(setIsLocalHold(true))
  }

  resumeCall = (id = this.activeCallId) => {
    const call = this.getCallById(id)
    if (!call) return
    if (id !== this.activeCallId) {
      this.switchToCall(id)
      return
    }
    this.setCallMediaEnabled(call, true)
    dispatchCustomEvent(CUSTOM_EVENT_TYPES.unmuteCall)
    store.dispatch(updateLocalCall({ id, isOnHold: false }))
    store.dispatch(setIsLocalHold(false))
  }

  /* =========================
     AUDIO OUTPUT (speaker)
  ========================== */

  isOutputSelectionSupported() {
    return !!this.device?.audio?.isOutputSelectionSupported
  }

  getOutputDevices() {
    const available = this.device?.audio?.availableOutputDevices
    if (!available) return []
    return Array.from(available.values()).map((d) => ({ deviceId: d.deviceId, label: d.label }))
  }

  getSelectedOutputDeviceId() {
    const selected = this.device?.audio?.speakerDevices?.get()
    if (!selected || !selected.size) return 'default'
    return Array.from(selected.values())[0]?.deviceId || 'default'
  }

  async setOutputDevice(deviceId) {
    if (!this.isOutputSelectionSupported()) return false
    try {
      await this.device.audio.speakerDevices.set(deviceId)
      await this.device.audio.ringtoneDevices.set(deviceId)
      for (const device of this.secondaryDevices.values()) {
        await device.audio?.speakerDevices.set(deviceId)
      }
      return true
    } catch (err) {
      console.error('❌ Failed to set output device', err)
      toastService.show('error', 'Cannot change audio output')
      return false
    }
  }

  /* =========================
     LOGOUT
  ========================== */

  logOut() {
    this.isLoggingOut = true
    this.hangupAll()
    this.device?.removeAllListeners()
    this.device?.destroy()
    this.device = null
    this.calls.clear()
    this.activeCallId = null
    this.tokenPromise = null
    this.isInitialized = false
    this.isLoggingOut = false
  }
}

const initTwilioVoice = new InitTwilioVoiceSingleton()
export default initTwilioVoice
